import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { OrderMode, DeliveryAddress, CustomerInfo } from '@/types';
import type { SelectedOption } from '@/types/cart';
import type { PaymentChannel } from '@/types/order';

interface CheckoutState {
  restaurantUuid: string;
  mode: OrderMode;
  customer: CustomerInfo;
  address: DeliveryAddress;
  scheduledAt: string | 'asap';
  payment: { channel: PaymentChannel };
  customerNote: string;
  /** SC-739 — réponses aux questions posées avant validation (couverts,
   *  sauce). Une question sans réponse cochée est simplement absente : c'est
   *  ce qui garde les couverts en opt-in. */
  cartOptions: SelectedOption[];
  /** Fidélité v2 (SC-1091) — the advantage code that leaves with the order
   *  ('' = none). Lives here, not in the page, so the cart drawer can preselect
   *  a wallet coupon before the customer reaches checkout. Not persisted, and
   *  never in a URL: only the server ever sees it. */
  redemptionCode: string;
}

const initialState: CheckoutState = {
  restaurantUuid: '',
  mode: 'takeaway',
  customer: { name: '', phone: '', email: '' },
  address: { street: '', city: '', zip: '' },
  scheduledAt: 'asap',
  payment: { channel: 'online' },
  customerNote: '',
  cartOptions: [],
  redemptionCode: '',
};

const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    setMode(state, a: PayloadAction<OrderMode>) { state.mode = a.payload; },
    setCustomer(state, a: PayloadAction<Partial<CustomerInfo>>) { Object.assign(state.customer, a.payload); },
    setAddress(state, a: PayloadAction<Partial<DeliveryAddress>>) { Object.assign(state.address, a.payload); },
    setScheduledAt(state, a: PayloadAction<string | 'asap'>) { state.scheduledAt = a.payload; },
    setChannel(state, a: PayloadAction<PaymentChannel>) { state.payment.channel = a.payload; },
    setCustomerNote(state, a: PayloadAction<string>) { state.customerNote = a.payload; },
    // Remplace la réponse d'UNE question. Une liste d'options vide retire la
    // question de l'état plutôt que d'y laisser une entrée vide, que le devis
    // aurait à filtrer.
    setCartOption(state, a: PayloadAction<SelectedOption>) {
      const rest = state.cartOptions.filter((s) => s.groupId !== a.payload.groupId);
      state.cartOptions = a.payload.options.length ? [...rest, a.payload] : rest;
    },
    clearCartOptions(state) { state.cartOptions = []; },
    setRedemptionCode(state, a: PayloadAction<string>) { state.redemptionCode = a.payload; },
    // Switches the checkout context to a different restaurant. Mode,
    // address, schedule, payment and note are restaurant-specific and get
    // wiped; `customer` (name/phone/email) is the consumer's personal info
    // and is preserved across restaurants.
    bindRestaurant(state, a: PayloadAction<string>) {
      if (state.restaurantUuid === a.payload) return;
      const keepCustomer = state.customer;
      Object.assign(state, initialState);
      state.restaurantUuid = a.payload;
      state.customer = keepCustomer;
    },
    resetCheckout() { return initialState; },
  },
});

export const {
  setMode,
  setCustomer,
  setAddress,
  setScheduledAt,
  setChannel,
  setCustomerNote,
  setCartOption,
  clearCartOptions,
  setRedemptionCode,
  bindRestaurant,
  resetCheckout,
} = checkoutSlice.actions;
export default checkoutSlice.reducer;

export const selectCheckout = (state: { checkout: CheckoutState }) => state.checkout;
